import React, { useRef, useState } from 'react';
import { Loader2, LogIn, Upload } from 'lucide-react';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import { supabase } from '../../integrations/supabase/client';
import { getAssetPath } from '../../utils/assetPath';

type UploadedWallpaper = {
  name: string;
  path: string;
  url: string;
};

interface WallpaperUploadPanelProps {
  userId: string | null;
  currentWallpaperUrl?: string;
  currentWallpaperName?: string;
  onUploaded: (wallpaper: UploadedWallpaper) => void;
}

const MAX_WALLPAPER_SIZE = 10 * 1024 * 1024;
const WALLPAPER_BUCKET = 'user-wallpapers';

export default function WallpaperUploadPanel({
  userId,
  currentWallpaperUrl,
  currentWallpaperName,
  onUploaded,
}: WallpaperUploadPanelProps) {
  const { toast } = useToast();
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    if (!userId) {
      toast({
        title: 'Sign in required',
        description: 'Please sign in to upload your own wallpaper.',
        variant: 'destructive',
      });
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid wallpaper file',
        description: 'Please choose an image file.',
        variant: 'destructive',
      });
      return;
    }

    if (file.size > MAX_WALLPAPER_SIZE) {
      toast({
        title: 'Wallpaper too large',
        description: 'Please choose an image smaller than 10MB.',
        variant: 'destructive',
      });
      return;
    }

    setIsUploading(true);

    try {
      const extension = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const path = `${userId}/desktop-wallpaper.${extension}`;

      const { error: uploadError } = await supabase.storage
        .from(WALLPAPER_BUCKET)
        .upload(path, file, {
          cacheControl: '3600',
          contentType: file.type || undefined,
          upsert: true,
        });

      if (uploadError) {
        throw uploadError;
      }

      const publicUrl = supabase.storage.from(WALLPAPER_BUCKET).getPublicUrl(path).data.publicUrl;
      const versionedUrl = `${publicUrl}?v=${Date.now()}`;

      onUploaded({
        name: file.name,
        path,
        url: versionedUrl,
      });
    } catch (error) {
      const description = error instanceof Error ? error.message : 'Unable to upload wallpaper right now.';
      toast({
        title: 'Wallpaper upload failed',
        description,
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="mb-6 rounded-2xl border border-dashed border-border/70 bg-muted/20 p-4">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-1.5">
          <div className="text-sm font-semibold text-foreground">Upload your own wallpaper</div>
          <div className="text-sm text-muted-foreground">
            Sync a personal desktop background through your MapleOS account.
          </div>
          {!userId && (
            <div className="inline-flex items-center gap-2 rounded-md border border-amber-300/50 bg-amber-500/10 px-3 py-1.5 text-xs text-amber-700 dark:text-amber-300">
              <LogIn className="h-3.5 w-3.5" />
              Sign in to upload and sync your wallpaper.
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          {currentWallpaperUrl ? (
            <div className="flex items-center gap-3 rounded-xl border border-border/60 bg-background/70 p-2 pr-3">
              <img
                src={getAssetPath(currentWallpaperUrl)}
                alt={currentWallpaperName || 'Custom wallpaper preview'}
                className="h-14 w-24 rounded-lg object-cover"
              />
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-foreground">
                  {currentWallpaperName || 'My Wallpaper'}
                </div>
                <div className="text-xs text-muted-foreground">Current uploaded wallpaper</div>
              </div>
            </div>
          ) : null}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            className="hidden"
            onChange={handleFileChange}
          />
          <Button
            type="button"
            variant="outline"
            className="min-w-[180px]"
            disabled={!userId || isUploading}
            onClick={() => fileInputRef.current?.click()}
          >
            {isUploading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4" />
                {currentWallpaperUrl ? 'Replace Wallpaper' : 'Upload Wallpaper'}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
